// src/components/ui/hover-card.js
//
// HoverCard — a popover that opens on hover (lib/hoverCard.js is the state
// machine). No dependency: portal to document.body so a clipping or
// animating parent (the rail) cannot cut it, placement right-start with
// viewport clamping, re-measured every frame while open (it follows the
// rail's width animation).
//
// Trigger + card are ONE interaction region: leaving the trigger for the
// card keeps it open. Keyboard: Enter / Space / ArrowDown on the trigger
// opens it and moves focus to the first item; Escape closes and returns
// focus; Tab past the last item closes it. While keyboard focus is inside
// the region a pointer leave does not close it. A pointer dismissal never moves
// focus. Only devices that can hover get a hover card (hover: hover and
// pointer: fine); elsewhere the trigger is just its own click.
//
//   <HoverCard label="Account" onOpenChange={setOpen}
//     trigger={(props) => <button {...props} onClick={go}>…</button>}>
//     {({ close }) => <Content onNavigate={close} />}
//   </HoverCard>

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { createHoverCard } from '../../lib/hoverCard';

const FOCUSABLE = 'a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])';
const GAP = 8, MARGIN = 8;

export function canHover() {
  try { return window.matchMedia('(hover: hover) and (pointer: fine)').matches; } catch { return false; }
}

export default function HoverCard({ trigger, children, label, onOpenChange, className = '' }) {
  const id = useId();
  const contentId = `hovercard-${id.replace(/:/g, '')}`;
  const triggerRef = useRef(null);
  const contentRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ left: -9999, top: -9999 });
  const [hoverable] = useState(canHover);
  const focusFirst = useRef(false);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const machine = useRef(null);
  if (!machine.current) {
    machine.current = createHoverCard({
      schedule: (fn, ms) => window.setTimeout(fn, ms),
      cancel: (t) => window.clearTimeout(t),
      onChange: (next, { returnFocus }) => {
        setOpen(next);
        onOpenChangeRef.current?.(next);
        if (!next && returnFocus) triggerRef.current?.focus();
      },
    });
  }
  const hc = machine.current;
  useEffect(() => () => hc.dispose(), [hc]);

  // right-start of the trigger, clamped into the viewport; follows the rail animation
  useLayoutEffect(() => {
    if (!open) return undefined;
    let raf = 0;
    const place = () => {
      const t = triggerRef.current?.getBoundingClientRect();
      const c = contentRef.current?.getBoundingClientRect();
      if (t && c) {
        const left = Math.min(t.right + GAP, window.innerWidth - c.width - MARGIN);
        const top = Math.max(MARGIN, Math.min(t.top, window.innerHeight - c.height - MARGIN));
        setPos((p) => (p.left === left && p.top === top ? p : { left, top }));
      }
      raf = window.requestAnimationFrame(place);
    };
    place();
    return () => window.cancelAnimationFrame(raf);
  }, [open]);

  // keyboard-opened: focus the first item once it is rendered
  useEffect(() => {
    if (open && focusFirst.current) {
      focusFirst.current = false;
      contentRef.current?.querySelector(FOCUSABLE)?.focus();
    }
  }, [open]);

  // outside click (pointer dismissal: no focus move)
  useEffect(() => {
    if (!open) return undefined;
    const down = (e) => {
      if (triggerRef.current?.contains(e.target) || contentRef.current?.contains(e.target)) return;
      hc.outsideClick();
    };
    document.addEventListener('pointerdown', down, true);
    return () => document.removeEventListener('pointerdown', down, true);
  }, [open, hc]);

  const close = useCallback(() => hc.outsideClick(), [hc]);
  const inRegion = (node) => !!node && (triggerRef.current?.contains(node) || contentRef.current?.contains(node));

  const triggerProps = hoverable ? {
    ref: triggerRef,
    'aria-expanded': open,
    'aria-controls': open ? contentId : undefined,
    'aria-haspopup': 'dialog',
    onPointerEnter: (e) => { if (e.pointerType === 'mouse') hc.pointerEnter(); },
    onPointerLeave: (e) => { if (e.pointerType === 'mouse') hc.pointerLeave(); },
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        if (!open) focusFirst.current = true;
        hc.toggle();
      } else if (e.key === 'Escape') {
        hc.escape();
      } else if (e.key === 'Tab' && open && !e.shiftKey) {
        const first = contentRef.current?.querySelector(FOCUSABLE);
        if (first) { e.preventDefault(); first.focus(); }
      }
    },
    onFocus: () => hc.focusIn(),
    onBlur: (e) => {
      const into = inRegion(e.relatedTarget);
      hc.focusOut({ intoRegion: into });
      hc.blur({ intoContent: into });
    },
  } : { ref: triggerRef };

  return (
    <>
      {trigger(triggerProps)}
      {hoverable && open && createPortal(
        <div
          ref={contentRef}
          id={contentId}
          role="dialog"
          aria-label={label}
          style={{ position: 'fixed', left: pos.left, top: pos.top }}
          className={`z-[90] w-[300px] rounded-[20px] border border-white/[0.08] bg-[#141414] text-white shadow-[0_32px_100px_rgba(0,0,0,0.38)] ${className}`}
          onPointerEnter={() => hc.contentEnter()}
          onPointerLeave={() => hc.contentLeave()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.stopPropagation(); hc.escape(); return; }
            if (e.key === 'Tab') {
              const items = [...contentRef.current.querySelectorAll(FOCUSABLE)];
              const atEnd = !e.shiftKey && document.activeElement === items[items.length - 1];
              const atStart = e.shiftKey && document.activeElement === items[0];
              if (atStart) { e.preventDefault(); triggerRef.current?.focus(); }
              else if (atEnd) { e.preventDefault(); hc.escape(); }
            }
          }}
          onFocus={() => hc.focusIn()}
          onBlur={(e) => {
            const into = inRegion(e.relatedTarget);
            hc.focusOut({ intoRegion: into });
            if (!into) hc.blur({ intoContent: false });
          }}
        >
          {children({ close })}
        </div>,
        document.body,
      )}
    </>
  );
}
